import { Container, Grid } from '@material-ui/core';
import useLanguageStore from '@stores/languageStore';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import CategoryFilter from '../../components/gallery/CategoryFilter';
import EmptyState from '../../components/gallery/EmptyState';
import GalleryImage from '../../components/gallery/GalleryImage';
import Lightbox from '../../components/gallery/Lightbox';
import SEOHead from '../../components/SEOHead';
import SectionHeader from '../../components/SectionHeader';
import { allImages, galleryCategories } from '../../data/galleryData';

const GalleryPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguageStore();
  const isRtl = language === 'he';
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFromUrl = searchParams.get('category') || 'all';
  const [selectedCategory, setSelectedCategory] = useState(categoryFromUrl);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    setSelectedCategory(categoryFromUrl);
  }, [categoryFromUrl]);

  const handleCategoryChange = (categoryId) => {
    setSelectedCategory(categoryId);
    if (categoryId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
  };

  const getFilteredImages = useCallback(() => {
    if (selectedCategory === 'all') {
      return allImages;
    }
    const category = galleryCategories.find((cat) => cat.id === selectedCategory);
    return category ? category.images : [];
  }, [selectedCategory]);

  const filteredImages = getFilteredImages();

  const openLightbox = (image, index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const allCategories = [
    { id: 'all', count: allImages.length },
    ...galleryCategories.map((cat) => ({
      id: cat.id,
      count: cat.images.length,
    })),
  ];

  const emptyMessage = t('gallery.emptyMessage');

  return (
    <div
      className={`min-h-[calc(100vh-64px)] bg-stone-50 py-20 sm:py-32 max-w-[100vw] overflow-x-hidden ${
        isRtl ? 'rtl' : 'ltr'
      }`}
    >
      <SEOHead titleKey="gallery.meta.title" descriptionKey="gallery.meta.description" />
      <Container maxWidth="xl" className="px-4 sm:px-8">
        <SectionHeader
          title={t('gallery.title')}
          subtitle={t('gallery.introText')}
          className="mb-12"
          component="h1"
        />
        <CategoryFilter
          categories={allCategories}
          selectedCategory={selectedCategory}
          onCategoryChange={handleCategoryChange}
          t={t}
        />

        {filteredImages.length > 0 ? (
          <Grid container spacing={3}>
            {filteredImages.map((image, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                <GalleryImage image={image} index={index} onImageClick={openLightbox} t={t} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <EmptyState message={emptyMessage} />
        )}

        <Lightbox
          open={lightboxOpen}
          onClose={closeLightbox}
          images={filteredImages}
          initialIndex={lightboxIndex}
          t={t}
        />
      </Container>
    </div>
  );
};

export default GalleryPage;
